import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Loader2, ShieldCheck, AlertCircle, ArrowUpRight, PenLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startStripeCheckout } from "@/lib/payments";
import { CinematicBackdrop } from "@/components/CinematicBackdrop";
import { clipFor, posterFor } from "@/lib/journey";
import { CURRICULUM } from "@/lib/curriculum";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GUMROAD_BUNDLE = "https://richculm.gumroad.com/l/lkejj";

const MODULES = {
  ...Object.fromEntries(CURRICULUM.map((m) => [m.id, { n: m.n, title: m.title, duration: m.duration, focus: m.focus }])),
  graduation: { n: "★", title: "You Made It Through", duration: "Graduation", focus: "All 8 modules complete" },
};

export default function ModuleWatchPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | forbidden | missing | error
  const [videoUrl, setVideoUrl] = useState(null);
  const [actualDur, setActualDur] = useState(null);
  const objectUrlRef = useRef(null);

  const module = MODULES[id];
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!module) { setState("error"); return; }
    if (user === null) return; // auth still resolving — wait so entitlement checks use the real user
    let alive = true;
    const load = async () => {
      setActualDur(null);
      try {
        if (id === "graduation" && !isAdmin) {
          const owns = user?.entitlements || [];
          const hasAccess = ["bundle", "course", "modules"].some((k) => owns.includes(k));
          if (!hasAccess) { setState("forbidden"); return; }
          const { data: prog } = await axios.get(`${API}/progress/me`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!prog.graduated) { setState("forbidden"); return; }
        }
        const res = await axios.get(`${API}/modules/${id}/video`, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!alive) return;
        const url = URL.createObjectURL(res.data);
        objectUrlRef.current = url;
        setVideoUrl(url);
        setState("ready");
      } catch (err) {
        if (!alive) return;
        const code = err.response?.status;
        if (code === 401 || code === 403) setState("forbidden");
        else if (code === 404) setState("missing");
        else setState("error");
      }
    };
    load();
    return () => {
      alive = false;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [id, module, token, user, isAdmin]);

  if (!module) {
    return (
      <main className="min-h-screen pt-32 pb-24 flex items-center justify-center px-6" data-testid="module-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl md:text-5xl mb-4">Module not found.</h1>
          <button onClick={() => navigate("/modules")} className="btn-outline text-sm mx-auto">
            <ArrowLeft size={14} /> Back to Modules
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen pt-32 md:pt-40 pb-24 overflow-hidden" data-testid="module-watch-page">
      {id === "graduation" ? (
        <CinematicBackdrop opacity={0.1} />
      ) : (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="module-scene-backdrop">
          <video src={clipFor(id)} poster={posterFor(id)} autoPlay muted loop playsInline className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/60 via-[#FFFFFF]/80 to-[#FFFFFF]" />
        </div>
      )}
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10">
        <button onClick={() => navigate(-1)} className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] hover:text-[#B45309] mb-10 inline-flex items-center gap-2 transition-colors" data-testid="module-back">
          <ArrowLeft size={12} /> Back
        </button>

        <div className="mb-12">
          <div className="section-tag mb-4">§ Module {module.n} · {module.focus}</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight mb-4">{module.title}</h1>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]">{actualDur || module.duration}</p>
          {isAdmin && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#F59E0B]/40 bg-[#F59E0B]/5 font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309]" data-testid="module-admin-badge">
              <ShieldCheck size={12} /> Admin preview
            </div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
          className="tacc-card overflow-hidden aspect-video flex items-center justify-center bg-black"
          data-testid="module-video-frame"
        >
          {state === "loading" && (
            <div className="flex flex-col items-center gap-3 text-[#78716C]">
              <Loader2 size={24} className="animate-spin text-[#B45309]" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Loading video…</span>
            </div>
          )}
          {state === "ready" && videoUrl && (
            <video
              data-testid="module-video-player"
              src={videoUrl}
              controls
              controlsList="nodownload"
              disablePictureInPicture
              className="w-full h-full"
              onLoadedMetadata={(e) => {
                const s = e.target.duration;
                if (s && isFinite(s)) setActualDur(`${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
          {state === "forbidden" && (
            <div className="text-center max-w-md px-6" data-testid="module-forbidden">
              <Lock size={28} className="mx-auto mb-4 text-[#B45309]" strokeWidth={1.4} />
              <h3 className="font-display text-2xl md:text-3xl mb-3">Locked.</h3>
              {id === "graduation" ? (
                <p className="text-[#78716C] text-sm" data-testid="graduation-locked-msg">Finish all 8 modules on your dashboard to unlock your graduation video.</p>
              ) : (
                <>
                  <p className="text-[#78716C] text-sm mb-6">Unlock all 8 modules with the Complete Bundle — $77 one-time, instant access. You buy direct and every dollar goes to Rich.</p>
                  <button onClick={() => startStripeCheckout("bundle", user?.id)} className="btn-ember text-sm mx-auto" data-testid="module-unlock-btn">
                    Unlock the Bundle · $77
                  </button>
                  <a href={GUMROAD_BUNDLE} target="_blank" rel="noreferrer" className="block mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] hover:text-[#B45309] transition-colors" data-testid="module-gumroad-locked">
                    Or get the full video of Rich talking on Gumroad <ArrowUpRight size={11} className="inline" />
                  </a>
                </>
              )}
            </div>
          )}
          {state === "missing" && (
            <div className="text-center max-w-md px-6" data-testid="module-missing">
              <AlertCircle size={28} className="mx-auto mb-4 text-[#B45309]" strokeWidth={1.4} />
              <h3 className="font-display text-2xl md:text-3xl mb-3">Coming soon.</h3>
              <p className="text-[#78716C] text-sm">This module&apos;s full video isn&apos;t uploaded yet. Rich is preparing it — check back shortly.</p>
              {isAdmin && (
                <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309]">Upload it in /admin → Content Preview</p>
              )}
            </div>
          )}
          {state === "error" && (
            <div className="text-center px-6" data-testid="module-error">
              <p className="text-[#78716C] text-sm">Something got in the way. Try refreshing the page.</p>
            </div>
          )}
        </motion.div>

        {state === "ready" && id !== "graduation" && (
          <div className="mt-6 flex items-start gap-3 bg-[#FFFDF9] border border-[#E7DFD5] p-5 md:p-6" data-testid="module-pause-tip">
            <PenLine size={16} className="text-[#B45309] mt-0.5 shrink-0" />
            <p className="text-[#78716C] text-sm leading-relaxed">
              <span className="text-[#44403C] font-medium">Pause after each question</span> and write your answer before moving on — that&apos;s where the healing happens. Your answers save automatically in your Workbook.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
